import { getTokenData } from '@/utils/token';
import { decodeToken } from 'jsontokens';
import {
    API_ENTRYPOINT_V1,
    API_ENTRYPOINT_URI,
    REFRESH_TOKEN_KEY,
    TOKEN_CONNECTED,
    TOKEN_KEY,
    TOKEN_WITHOUT_ROLES_KEY,
} from '@/utils/CONST';
import axios from 'axios';

interface AuthProviderLocalState {
    user: {
        data: any;
        refreshToken: string | null;
        loggedIn: boolean;
    };
}

export const localState: AuthProviderLocalState = {
    user: {
        data: null,
        refreshToken: null,
        loggedIn: false,
    },
};

export const isValidToken = (token: string) => {
    if (!token) return false;

    try {
        const tokenData: any = decodeToken(token);
        const current_time = Date.now() / 1000;
        return tokenData && tokenData.payload.exp >= current_time;
    } catch (e) {
        return false;
    }
};

const authProvider = {
    login: async (params: { username: string; password: string }) => {
        const formData = new FormData();
        formData.set('_username', params.username);
        formData.set('_password', params.password);

        const { data: data1 } = await axios.post(`${API_ENTRYPOINT_V1}/${API_ENTRYPOINT_URI}/login_check`, formData);

        const token = data1?.token;
        const refreshToken = data1?.refresh_token;

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

        const { data: data2 } = await axios.get(`${API_ENTRYPOINT_V1}/${API_ENTRYPOINT_URI}/${TOKEN_CONNECTED}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        const tokenWithoutRoles = data2?.token_without_roles;

        localStorage.setItem(TOKEN_WITHOUT_ROLES_KEY, tokenWithoutRoles);

        return Promise.resolve();
    },

    anonymousLogin: (): Promise<{ token: string; refreshToken: string } | undefined> => {
        const formData = new FormData();
        formData.set('_username', import.meta.env.VITE_SHORT_TO_ANONYMOUS_TOKEN ?? '');
        formData.set('_password', import.meta.env.VITE_UNIQUE_VALUE ?? '');
        return fetch(
            new Request(
                `${API_ENTRYPOINT_V1}/${API_ENTRYPOINT_URI}/optimized_token_semi_anonymous_easy_login_check`,
                {
                method: 'POST',
                body: formData,
                },
            ),
        )
            .catch((error: any) => console.error(error))
            .then((response: any) => {
                if ('status' in response && response.status === 404) return undefined;
                if ('json' in response) {
                    return response.json();
                }
                return undefined;
            })
            .then((data) => {
                if (data) {
                    const { token, refresh_token } = data;
                    if (token && refresh_token) {
                        // @ts-ignore
                        localStorage.setItem(TOKEN_KEY, token);
                        // @ts-ignore
                        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

                        return Promise.resolve({ refreshToken: refresh_token, token });
                    } else {
                        return undefined;
                    }
                }

                return undefined;
            });
    },

    refreshToken: async (params: { refreshToken: string }) => {
        const { data: data1 } = await axios.post(`${API_ENTRYPOINT_V1}/${API_ENTRYPOINT_URI}/token/refresh`, {
            refresh_token: params.refreshToken,
        });

        const token = data1?.token;
        const refreshToken = data1?.refresh_token;

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

        const { data: data2 } = await axios.get(`${API_ENTRYPOINT_V1}/${API_ENTRYPOINT_URI}/${TOKEN_CONNECTED}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        const tokenWithoutRoles = data2?.token_without_roles;

        localStorage.setItem(TOKEN_WITHOUT_ROLES_KEY, tokenWithoutRoles);

        return Promise.resolve();
    },

    logout: (): Promise<void | string> => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_WITHOUT_ROLES_KEY);
        return Promise.resolve('/login');
    },

    checkAuth: async (): Promise<void> => {
        const is_valid = isValidToken(localStorage.getItem(TOKEN_KEY) ?? '');
        return is_valid ? Promise.resolve() : Promise.reject();
    },

    checkError: (error: any): Promise<void> => {
        let status: number | null = null;
        
        if (error && typeof error === 'object') {
            if ('status' in error) {
                status = error.status;
            } else if (error.response && typeof error.response === 'object' && 'status' in error.response) {
                status = error.response.status;
            }
        }
        
        if (status) {
            if (status === 401 || status === 403) {
                localState.user.loggedIn = false;

                return Promise.reject({ redirectTo: '/' });
            }
        }

        return Promise.resolve();
    },

    getPermissions: async (): Promise<string[]> => {
        const token = localStorage.getItem(
            // @ts-ignore
            TOKEN_KEY,
        );

        if (!token) return Promise.reject({ redirectTo: '/login' });

        const tokenData = getTokenData(token);

        return Promise.resolve(Object.values(tokenData?.roles));
    },

    getIdentity: async (): Promise<any> => {
        const is_valid = isValidToken(localStorage.getItem(TOKEN_KEY) ?? '');

        if (is_valid) {
            const tokenData: any = decodeToken(localStorage.getItem(TOKEN_KEY) ?? '').payload;
            return Promise.resolve({ ...tokenData });
        }
        return Promise.reject();
    },
    // getIdentity: () => {
    //     return getUserConnected().then((user: DUser) => user);
    // }
};

export default authProvider;
